import React from 'react'
import { usePersistedState } from '@cbgms/base/hooks/persisted-state/use-persisted-state'
import { IPaginatedRequest, IPagination, Pagination } from '@cbgms/api'
import isEqual from 'lodash/isEqual'

interface IUsePaginationManagerResult {
  handlePageChange: (loadData: (additionalParams?: IPaginatedRequest | undefined) => Promise<void>, page: number) => Promise<void>
  handleLimitChange: (loadData: (additionalParams?: IPaginatedRequest | undefined) => Promise<void>, limit: number) => Promise<void>
  setPaginationProperty: (args: ISetPaginationProperty) => void
  getPaginationProperty: (propertyName: Pagination) => number | undefined
}

interface IUsePaginationManager {
  scope: string
  getUserID: () => string
}

export type ISetPaginationProperty = {
  [propertyName in Pagination]?: number
}

export const usePaginationManager = ({ scope, getUserID }: IUsePaginationManager): IUsePaginationManagerResult => {
  const userID = getUserID()
  const [localGridPagination, setLocalGridPagination] = usePersistedState<IPagination>(`grid-pagination_${scope}_${userID}`, {})

  const getPaginationProperty = (propertyName: Pagination): number | undefined => {
    return localGridPagination?.[propertyName]
  }

  const setPaginationProperty = (args: ISetPaginationProperty): void => {
    const newGridPagination = {
      ...localGridPagination,
      ...args
    }

    if (!isEqual(localGridPagination, newGridPagination)) {
      setLocalGridPagination(newGridPagination)
    }
  }

  const getPageForCurrentData = (limit: number) => {
    const dataLength = getPaginationProperty(Pagination.Total) || 0
    const currentPage = getPaginationProperty(Pagination.Page) || 1
    const maxPage = Math.ceil(dataLength / limit)

    if (currentPage > maxPage) {
      return maxPage
    }
    return currentPage
  }

  const handlePageChange = async (loadData: (additionalParams?: IPaginatedRequest | undefined) => Promise<void>, page: number) => {
    setPaginationProperty({ Page: page })
    await loadData({ page })
  }

  const handleLimitChange = async (loadData: (additionalParams?: IPaginatedRequest | undefined) => Promise<void>, limit: number) => {
    const page = getPageForCurrentData(limit)
    setPaginationProperty({ Page: page, PerPage: limit })
    await loadData({ perPage: limit, page })
  }

  return React.useMemo(
    () => ({
      handlePageChange,
      handleLimitChange,
      setPaginationProperty,
      getPaginationProperty
    }),
    [handlePageChange, handleLimitChange, setPaginationProperty, getPaginationProperty]
  )
}
